import { Component, ChangeDetectionStrategy, inject, signal, computed, Signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth } from '../../../../services/auth';
import { ToastrService } from 'ngx-toastr';

// Constants
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = {
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBER: /\d/,
  SPECIAL_CHAR: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/
};

// Interfaces
interface PasswordFormData {
  currentPassword: FormControl<string | null>;
  newPassword: FormControl<string | null>;
  confirmPassword: FormControl<string | null>;
}

interface PasswordRequirements {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

interface PasswordChangeResult {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-change-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './change-password.html',
  styleUrl: './change-password.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePassword implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<ChangePassword>);
  private authService = inject(Auth);
  private toastr = inject(ToastrService);
  // UI State
  hideCurrentPassword = signal(true);
  hideNewPassword = signal(true);
  hideConfirmPassword = signal(true);
  isLoading = signal(false);

  // Form
  passwordForm!: FormGroup<PasswordFormData>;

  // Reactive Signals
  newPasswordValue!: Signal<string | null>;
  confirmPasswordValue!: Signal<string | null>;
  passwordRequirements!: Signal<PasswordRequirements>;
  passwordsMatch!: Signal<boolean>;

  constructor() {
    this.initializeForm();
    this.initializeSignals();
  }

  ngOnInit(): void {
    // Component initialization complete
  }

  private initializeForm(): void {
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, this.passwordStrengthValidator.bind(this)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator.bind(this) });
  }

  private initializeSignals(): void {
    // Create reactive signals from form controls
    this.newPasswordValue = toSignal(
      this.passwordForm.get('newPassword')!.valueChanges,
      { initialValue: '' }
    );
    this.confirmPasswordValue = toSignal(
      this.passwordForm.get('confirmPassword')!.valueChanges,
      { initialValue: '' }
    );

    // Create computed signals
    this.passwordRequirements = computed(() => {
      const newPassword = this.newPasswordValue() || '';
      return {
        minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
        hasUpperCase: PASSWORD_REGEX.UPPERCASE.test(newPassword),
        hasLowerCase: PASSWORD_REGEX.LOWERCASE.test(newPassword),
        hasNumber: PASSWORD_REGEX.NUMBER.test(newPassword),
        hasSpecialChar: PASSWORD_REGEX.SPECIAL_CHAR.test(newPassword)
      };
    });

    this.passwordsMatch = computed(() => {
      const newPassword = this.newPasswordValue() || '';
      const confirmPassword = this.confirmPasswordValue() || '';
      return !!(newPassword && confirmPassword && newPassword === confirmPassword);
    });
  }

  private passwordStrengthValidator(control: AbstractControl) {
    const password = control.value;
    if (!password) return null;

    const hasMinLength = password.length >= PASSWORD_MIN_LENGTH;
    const hasUpperCase = PASSWORD_REGEX.UPPERCASE.test(password);
    const hasLowerCase = PASSWORD_REGEX.LOWERCASE.test(password);
    const hasNumber = PASSWORD_REGEX.NUMBER.test(password);
    const hasSpecialChar = PASSWORD_REGEX.SPECIAL_CHAR.test(password);

    const isValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
    return isValid ? null : { passwordStrength: true };
  }

  private passwordMatchValidator(group: AbstractControl) {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (!newPassword || !confirmPassword) return null;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  async onSubmit(): Promise<void> {
    if (!this.passwordForm.valid) {
      this.markAllFieldsAsTouched();
      return;
    }

    // get current user name:
    const currentUserName = this.authService.authState().user?.username;
    if (!currentUserName) {
      this.toastr.error('Hiba történt a jelszó megváltoztatása során!', 'Hiba');
      return;
    }

    this.authService.changePassword({
      identifier: currentUserName,
      oldPass: this.passwordForm.value.currentPassword!,
      newPass: this.passwordForm.value.newPassword!
    }).subscribe({
      next: () => {
        this.toastr.success('Jelszó megváltoztatva!', 'Sikeres művelet');
        this.dialogRef.close({ success: true });
      },
      error: (error) => {
        this.toastr.error(error.error?.message || 'Hiba történt a jelszó megváltoztatása során!', 'Hiba');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.passwordForm.controls).forEach(key => {
      this.passwordForm.get(key)?.markAsTouched();
    });
  }
}
